import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../services/supabase'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'

// "EMME001" → "emme" | "VAN014" → "van" | "DEV001" → "dev"
function getCodePrefix(code) {
  return (code || '').replace(/\d+$/, '').toLowerCase().replace(/\s+/g, '')
}

function matchByCodePrefix(code, clients) {
  const prefix = getCodePrefix(code)
  if (!prefix) return null

  const hits = clients.filter(c => {
    // strip spaces so "de Vries" → "devries" → startsWith("dev") ✓
    const sk = (c.surname || '').toLowerCase().replace(/\s+/g, '')
    const lastWord = (c.full_name || '').toLowerCase().split(/\s+/).pop() || ''
    return sk.startsWith(prefix) || lastWord.startsWith(prefix)
  })

  if (hits.length === 1) return { client: hits[0], confidence: 90, label: 'Code prefix' }
  if (hits.length > 1)  return { client: hits[0], confidence: 65, label: 'Code prefix — verify' }
  return null
}

function ConfidenceBadge({ row }) {
  if (row.alreadySet)         return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Already set</span>
  if (row.confidence >= 90)   return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{row.label}</span>
  if (row.confidence >= 65)   return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{row.label}</span>
  return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">No match</span>
}

export function ClientCodeMatcher({ onClose, onDone, sidebarCollapsed }) {
  const [loading, setLoading]   = useState(true)
  const [applying, setApplying] = useState(false)
  const [error, setError]       = useState('')
  const [clients, setClients]   = useState([])
  const [rows, setRows]         = useState([])
  const [step, setStep]         = useState('review')
  const [result, setResult]     = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [{ data: policyRows, error: pe }, { data: clientData, error: ce }] = await Promise.all([
        supabase.from('policies').select('customer_code').not('customer_code', 'is', null),
        supabase.from('clients').select('id, full_name, surname, customer_code'),
      ])

      if (pe) throw new Error(pe.message)
      if (ce) throw new Error(ce.message)

      const allClients = clientData || []
      setClients(allClients)

      const uniqueCodes = [...new Set((policyRows || []).map(p => p.customer_code).filter(Boolean))]
      const alreadySetCodes = new Set(allClients.filter(c => c.customer_code).map(c => c.customer_code))
      const unassigned = allClients.filter(c => !c.customer_code)

      const matched = uniqueCodes.map(code => {
        if (alreadySetCodes.has(code)) {
          const linkedClient = allClients.find(c => c.customer_code === code)
          return { customerCode: code, match: linkedClient, confidence: 100, label: 'Already set', alreadySet: true, selected: false, manualClientId: '' }
        }
        const res = matchByCodePrefix(code, unassigned)
        return {
          customerCode: code,
          match: res?.client || null,
          confidence: res?.confidence || 0,
          label: res?.label || 'No match',
          alreadySet: false,
          selected: res ? res.confidence >= 65 : false,
          manualClientId: '',
        }
      })

      // Sort: no match first, then by confidence desc
      matched.sort((a, b) => {
        if (a.alreadySet !== b.alreadySet) return a.alreadySet ? 1 : -1
        return b.confidence - a.confidence
      })

      setRows(matched)
      setStep('review')
    } catch (err) {
      setError(`Failed to load data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleApply = async () => {
    const toApply = rows.filter(r => !r.alreadySet && r.selected)
    if (!toApply.length) { setError('No rows selected.'); return }
    setApplying(true)

    // Resolve clientId for each row upfront, skip rows with no target
    const valid = toApply
      .map(r => ({ row: r, clientId: r.manualClientId || r.match?.id }))
      .filter(({ clientId }) => !!clientId)
    const skipped = toApply.length - valid.length

    // Run all client updates in parallel
    const clientResults = await Promise.all(
      valid.map(({ row, clientId }) =>
        supabase.from('clients')
          .update({ customer_code: row.customerCode })
          .eq('id', clientId)
          .then(({ error }) => ({ clientId, code: row.customerCode, ok: !error }))
      )
    )

    const succeeded = clientResults.filter(r => r.ok)
    const failed = clientResults.filter(r => !r.ok).length + skipped

    // Run all policy link updates in parallel (only for successful client updates)
    await Promise.all(
      succeeded.map(({ clientId, code }) =>
        supabase.from('policies')
          .update({ client_id: clientId })
          .eq('customer_code', code)
      )
    )

    setResult({ linked: succeeded.length, failed })
    setStep('done')
    setApplying(false)
  }

  const toDoRows  = rows.filter(r => !r.alreadySet)
  const alreadyN  = rows.filter(r =>  r.alreadySet).length
  const selectedN = toDoRows.filter(r => r.selected).length
  const noMatchN  = toDoRows.filter(r => !r.match && !r.manualClientId).length

  const toggleAll = (checked) =>
    setRows(prev => prev.map(r => r.alreadySet ? r : { ...r, selected: checked }))

  const toggleRow = (code, checked) =>
    setRows(prev => prev.map(r => r.customerCode === code ? { ...r, selected: checked } : r))

  const setManual = (code, clientId) =>
    setRows(prev => prev.map(r => r.customerCode === code
      ? { ...r, manualClientId: clientId, selected: !!clientId }
      : r))

  const unassignedClients = clients.filter(c => !c.customer_code)

  return (
    <Modal isOpen onClose={onClose} title="Match Client Codes" size="full" sidebarCollapsed={sidebarCollapsed}>
      <div className="flex flex-col gap-4" style={{ maxHeight: '78vh' }}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm flex-shrink-0">
            {error}
          </div>
        )}

        {/* ── LOADING ─────────────────────────────────────────────── */}
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500 text-sm">
            <svg className="animate-spin h-5 w-5 mr-2 text-sfp-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            Loading codes from database…
          </div>
        )}

        {/* ── REVIEW ─────────────────────────────────────────────── */}
        {!loading && step === 'review' && (
          <>
            {/* Summary pills */}
            <div className="flex gap-3 flex-shrink-0 flex-wrap items-center">
              <span className="px-3 py-1 bg-green-50 border border-green-200 rounded text-green-700 text-xs font-medium">
                {toDoRows.filter(r => r.confidence >= 90).length} code-prefix matches
              </span>
              <span className="px-3 py-1 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs font-medium">
                {toDoRows.filter(r => r.confidence >= 65 && r.confidence < 90).length} need verification
              </span>
              <span className="px-3 py-1 bg-red-50 border border-red-200 rounded text-red-700 text-xs font-medium">
                {noMatchN} no match — select manually
              </span>
              {alreadyN > 0 && (
                <span className="px-3 py-1 bg-blue-50 border border-blue-200 rounded text-blue-700 text-xs font-medium">
                  {alreadyN} already set (hidden)
                </span>
              )}
              <button
                onClick={loadData}
                className="ml-auto text-xs text-sfp-orange hover:underline"
              >
                ↻ Refresh
              </button>
            </div>

            {/* Table */}
            <div className="overflow-auto flex-1 border border-gray-200 rounded">
              <table className="w-full text-xs">
                <thead className="table-header sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox"
                        checked={toDoRows.length > 0 && toDoRows.every(r => r.selected)}
                        onChange={e => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Cust. Code</th>
                    <th className="px-3 py-2 text-left">Matched Client</th>
                    <th className="px-3 py-2 text-left">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {toDoRows.map((row, idx) => (
                    <tr key={row.customerCode} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={row.selected}
                          onChange={e => toggleRow(row.customerCode, e.target.checked)} />
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-sfp-orange">{row.customerCode}</td>
                      <td className="px-3 py-2">
                        {row.match
                          ? <span className="text-green-700 font-medium">{row.match.full_name}</span>
                          : (
                            <select
                              value={row.manualClientId}
                              onChange={e => setManual(row.customerCode, e.target.value)}
                              className="input-field text-xs py-0.5 w-full"
                            >
                              <option value="">— select client —</option>
                              {unassignedClients.map(c => (
                                <option key={c.id} value={c.id}>{c.full_name}</option>
                              ))}
                            </select>
                          )
                        }
                      </td>
                      <td className="px-3 py-2"><ConfidenceBadge row={row} /></td>
                    </tr>
                  ))}
                  {toDoRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-gray-400">
                        No unmatched codes found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex gap-2 justify-between items-center pt-2 border-t border-gray-200 flex-shrink-0">
              <span className="text-sm text-gray-600">{selectedN} of {toDoRows.length} selected</span>
              <Button variant="primary" onClick={handleApply} disabled={applying || selectedN === 0}>
                {applying ? 'Applying…' : `Apply ${selectedN} match${selectedN !== 1 ? 'es' : ''}`}
              </Button>
            </div>
          </>
        )}

        {/* ── DONE ───────────────────────────────────────────────── */}
        {step === 'done' && result && (
          <div className="text-center py-10 space-y-4">
            <div className="text-5xl">{result.failed === 0 ? '✅' : '⚠️'}</div>
            <p className="text-xl font-bold text-sfp-darkgray">
              {result.linked} client{result.linked !== 1 ? 's' : ''} linked
            </p>
            <p className="text-sm text-gray-500">
              Customer codes set on client records.<br />
              All matching policies are now linked to the correct client.
            </p>
            {result.failed > 0 && (
              <p className="text-sm text-amber-600">{result.failed} row{result.failed !== 1 ? 's' : ''} could not be applied.</p>
            )}
            <Button variant="primary" onClick={() => { onDone?.(); onClose() }}>Done</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}
